import React, { useMemo, useState } from 'react';
import {
  Table,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Button,
  Spin,
  Badge,
  message,
} from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getLocationRegistryMappings,
  createLocationRegistryMapping,
  getLocations,
} from '../../api/location';

const { Title, Text } = Typography;

type StatusType = 'mapped' | 'needs-mapping';

function getStatusFromCalyxCountry(calyxCountry: string | null | undefined): StatusType {
  return calyxCountry ? 'mapped' : 'needs-mapping';
}

function renderStatusTag(status: StatusType) {
  if (status === 'mapped') return <Tag color="green">Mapped</Tag>;
  return <Tag color="red">Needs Mapping</Tag>;
}

async function fetchLocationRegistryMappingsData() {
  const data = await getLocationRegistryMappings();
  const rows = Array.isArray(data) ? data : data.response || [];
  return rows.map((row: any, index: number) => ({
    ...row,
    id: row.id ?? `${row.registry_country || 'REG'}-${index}`,
    total_inventory:
      typeof row.total_inventory === 'string'
        ? Number(row.total_inventory)
        : row.total_inventory,
  }));
}

async function fetchLocationsData() {
  const data = await getLocations();
  const rows = Array.isArray(data) ? data : data.response || [];
  return rows;
}

async function updateLocationRegistryMapping(
  record: any,
  calyxCountry: string | null,
) {
  const payload = {
    registry_country: record.registry_country,
    calyx_country: calyxCountry,
  };

  await createLocationRegistryMapping(payload);

  return {
    ...record,
    calyx_country: calyxCountry,
  };
}

const locationMutationFn = ({
  record,
  calyxCountry,
}: {
  record: any;
  calyxCountry: string | null;
}) => updateLocationRegistryMapping(record, calyxCountry);

function LocationMapper() {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusType>('all');

  const {
    data: rows = [],
    isLoading: mappingsLoading,
    isFetching: mappingsFetching,
  } = useQuery({
    queryKey: ['locationRegistryMappings'],
    queryFn: fetchLocationRegistryMappingsData,
  });

  const {
    data: locations = [],
    isLoading: locationsLoading,
  } = useQuery({
    queryKey: ['locations'],
    queryFn: fetchLocationsData,
  });

  const {
    mutate: updateLocationMapping,
    isLoading: isUpdatingLocation,
  } = useMutation(locationMutationFn, {
    onSuccess: () => {
      queryClient.invalidateQueries(['locationRegistryMappings']);
      message.success('Location mapping updated');
    },
    onError: () => {
      message.error('Failed to update location mapping. Please try again.');
    },
  });

  const loading =
    mappingsLoading || mappingsFetching || locationsLoading || isUpdatingLocation;

  const calyxCountryOptions = useMemo(() => {
    const raw = Array.isArray(locations) ? locations : (locations as any)?.response || [];
    const countries = (raw as any[]).filter((loc) => loc.type === 'country');
    return countries.map((loc) => ({
      value: loc.display_name || loc.name,
      label: loc.display_name || loc.name,
    }));
  }, [locations]);

  const filteredRows = useMemo(() => {
    const text = search.toLowerCase().trim();
    return (rows as any[]).filter((row) => {
      const status = getStatusFromCalyxCountry(row.calyx_country);
      if (statusFilter !== 'all' && status !== statusFilter) return false;

      if (!text) return true;

      const haystack = [
        row.registry_country || '',
        row.calyx_country || '',
        row.total_inventory != null ? String(row.total_inventory) : '',
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(text);
    });
  }, [rows, search, statusFilter]);

  const handleCalyxCountryChange = (record: any, value: string | null) => {
    const newValue = value ?? null;
    updateLocationMapping({ record, calyxCountry: newValue });
  };

  const columns: any[] = [
    {
      title: 'Registry Country',
      dataIndex: 'registry_country',
      key: 'registry_country',
      width: 200,
    },
    {
      title: 'Calyx Country',
      dataIndex: 'calyx_country',
      key: 'calyx_country',
      width: 260,
      render: (value: string | null, record: any) => {
        const baseOptions = calyxCountryOptions;
        const options =
          value && !baseOptions.some((o) => o.value === value)
            ? [{ value, label: value }, ...baseOptions]
            : baseOptions;

        return (
          <Select
            allowClear
            placeholder="Select Calyx Country"
            value={value || undefined}
            options={options}
            onChange={(val) => handleCalyxCountryChange(record, val ?? null)}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
    {
      title: 'Total Inventory',
      dataIndex: 'total_inventory',
      key: 'total_inventory',
      width: 140,
      align: 'right',
      render: (value: number | null) => (value != null ? value.toLocaleString() : '—'),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_: any, record: any) => {
        const status = getStatusFromCalyxCountry(record.calyx_country);
        return renderStatusTag(status);
      },
    },
  ];

  const needsMappingCount = (rows as any[]).filter(
    (r) => getStatusFromCalyxCountry(r.calyx_country) === 'needs-mapping',
  ).length;

  return (
    <div
      style={{
        padding: 24,
        background: '#f5f5f5',
        minHeight: '100vh',
        boxSizing: 'border-box',
      }}
    >
      <Spin spinning={loading} tip="Loading location mappings…">
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              Location Mapper
            </Title>
            <Text type="secondary">
              Map registry countries to Calyx countries using the locations list.
            </Text>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              queryClient.invalidateQueries(['locationRegistryMappings']);
              queryClient.invalidateQueries(['locations']);
            }}
          >
            Refresh
          </Button>
        </div>

        <div
          style={{
            margin: '16px 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search registry country, calyx country, inventory…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 360 }}
          />
          <Space>
            <Text type="secondary">Show:</Text>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 220 }}
              options={[
                { value: 'all', label: 'All countries' },
                { value: 'needs-mapping', label: 'Needs mapping' },
                { value: 'mapped', label: 'Mapped' },
              ]}
            />
            <Badge
              count={needsMappingCount}
              size="small"
              style={{ backgroundColor: '#fa541c' }}
            />
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={filteredRows}
          rowKey="id"
          size="middle"
          pagination={{ pageSize: 50 }}
        />
      </Spin>
    </div>
  );
}

export default LocationMapper;
